import Clarity from '@microsoft/clarity'
import { getLogger } from 'app/components/logger'
import { ApplicationConfiguration } from '../../types'
import { User } from '../../../auth/types'
import { userStorage } from 'app/modules/auth/helper/storage'

let isClarityActivated = false
const logger = getLogger('Clarity:')

// Unique session ID per app load — resets on refresh/new tab
const claritySessionId = crypto.randomUUID()

// Init Microsoft Clarity
export function initClarity(config?: ApplicationConfiguration): void {
  if (!config?.clarityProjectId) {
    shutdownClarity()
    return
  }
  if (isClarityActivated) {
    logger.debug('Clarity already activated')
    return
  }

  logger.debug('Initialize Clarity with project ID:', config.clarityProjectId)
  Clarity.init(config.clarityProjectId)
  isClarityActivated = true
}

// Set the Clarity user context via Identify API
export function setClarityUser(user: User | undefined): void {
  if (!isClarityActivated) {
    return
  }

  const clientId = userStorage.getClientId() ?? userStorage.setClientId()
  const userId = user ? user.accountId : clientId
  const role = user ? (user.isAdmin ? 'admin' : user.isAuditor ? 'auditor' : 'user') : 'guest'

  logger.debug('Set Clarity user:', { userId, role })
  Clarity.identify(userId, claritySessionId)
  Clarity.setTag('role', role)
}

// Re-identify the user on page navigation (Clarity recommends calling identify on each page)
export function identifyClarityPage(userId?: string, pageId?: string): void {
  if (!isClarityActivated) {
    return
  }

  const identifyUserId = userId ?? userStorage.getClientId() ?? userStorage.setClientId()

  if (identifyUserId) {
    Clarity.identify(identifyUserId, claritySessionId, pageId)
  }
}

// Set Clarity consent via v2 API (separate ad and analytics consent via OneTrust)
export function setClarityConsent(analyticsGranted: boolean, adGranted: boolean): void {
  if (!isClarityActivated) {
    return
  }

  Clarity.consentV2({
    analytics_Storage: analyticsGranted ? 'granted' : 'denied',
    ad_Storage: adGranted ? 'granted' : 'denied',
  })
}

// Shutdown Clarity
export function shutdownClarity(): void {
  if (isClarityActivated) {
    logger.warn('Shut down Clarity monitoring')
    isClarityActivated = false
  }
}
